import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { NoSSR } from '@/components';
import { transformCurrentPlanOptions, useSubscribeUserPlan, UseSubscribeUserPlanKey, useUser } from '@/lib/auth';
import { NotifyPlanStartContainer } from '@/module/plan';
import { PlanExpirationAlertContainer } from './components/PlanExpirationAlertContainer';
import { checkPlanChangedAndReset, planExpirationAlertStorage, planStartAlertStorage } from './storage';
const PATHNAME_CONFIG = {
  // 구독 자체 비활성화 (full path로 매칭)
  subscriptionDisabled: ['/sign-up', '/login',
  // 학생코드/QR 로그인 진입 페이지. 계정전환(A→B) 시 이 페이지가 살아있는 동안 plan 구독이
  // updateUser ↔ planData 순환을 일으켜 #185(Maximum update depth) 무한루프가 난다. /login 과
  // 동일하게 인증 진입 페이지에선 구독을 끈다. startsWith('/login') 은 '/code-login' 을 매칭 안 함.
  '/code-login', '/admin', '/auth', '/play', '/trouble', '/build', '/workspace', '/plan', '/404', '/500', '/checkout/processing'],
  // 알림만 비활성화 (full path로 매칭)
  notificationDisabled: ['/checkout/complete']
};
export const SubscribeUserPlan = () => {
  const {
    isInitialized,
    user,
    updateUser
  } = useUser();
  const router = useRouter();
  const client = useQueryClient();
  const [planChangeVersion, setPlanChangeVersion] = useState(0);
  const {
    isDisabledPage,
    isNotificationDisabledPage
  } = useMemo(() => {
    const {
      pathname
    } = router;
    return {
      isDisabledPage: PATHNAME_CONFIG.subscriptionDisabled.some(path => pathname.startsWith(path)),
      isNotificationDisabledPage: PATHNAME_CONFIG.notificationDisabled.some(path => pathname.startsWith(path))
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.pathname]);
  const enabled = useMemo<boolean>(() => {
    if (isDisabledPage) {
      return false;
    }
    if (!isInitialized) return false;
    return user !== null;
  }, [isDisabledPage, isInitialized, user]);
  const {
    data: planData,
    isError,
    refetch
  } = useSubscribeUserPlan({
    enabled: enabled
  });
  useEffect(() => {
    // user가 없으면 planId 생성이 불완전하므로 건너뜀
    if (!user?.quizUserId || !planData?.data) {
      return;
    }
    const isPlanChanged = checkPlanChangedAndReset({
      userId: user.quizUserId.toString(),
      workspaceHashId: planData.data.workspace?.hashId,
      quizPlanType: planData.data.plan.planType,
      startDate: planData.data.plan.startDate,
      endDate: planData.data.plan.endDate
    }, [planExpirationAlertStorage, planStartAlertStorage]);
    if (isPlanChanged) {
      setPlanChangeVersion(version => version + 1);
    }
    updateUser({
      ...transformCurrentPlanOptions(planData.data)
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planData, user?.quizUserId]);
  useEffect(() => {
    // 구독이 비활성화된 경우 쿼리 캐시만 초기화
    // 알림 상태는 플랜 변경 시 자동으로 초기화되므로 여기서 삭제 불필요
    if (!enabled) {
      void client.resetQueries({
        queryKey: UseSubscribeUserPlanKey
      });
    }
  }, [client, enabled]);
  useEffect(() => {
    if (isError) {
      updateUser({
        planType: 'FREE',
        planStatus: 'UNKNOWN',
        upcomingPlan: null,
        planDuration: undefined
      });
    }
  }, [isError, updateUser]);
  useEffect(() => {
    const handleRouteChangeComplete = () => {
      if (!enabled) return;
      void refetch();
    };
    router.events.on('routeChangeComplete', handleRouteChangeComplete);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChangeComplete);
    };
  }, [enabled, refetch, router.events]);
  if (isNotificationDisabledPage || !planData || planData.data.plan.planStatus !== 'ACTIVE' || !planData.data.plan.endDate || !planData.data.workspace) {
    return null;
  }

  // 플랜 만료 알림 표시
  return <>
      <NotifyPlanStartContainer eventType={planData.data.workspace.eventType} planType={planData.data.plan.planType ?? 'FREE'} startDate={planData.data.plan.startDate ?? ''} endDate={planData.data.plan.endDate ?? ''} isPaid={planData.data.workspace.isPaidOwner} workspaceMemberType={planData.data.workspace.memberType ?? 'MEMBER'} planChangeVersion={planChangeVersion} data-sentry-element="NotifyPlanStartContainer" data-sentry-source-file="SubscribeUserPlan.tsx" />
      <PlanExpirationAlertContainer endDate={planData.data.plan.endDate} isDisabledPage={isDisabledPage} workspaceMemberType={planData?.data?.workspace.memberType ?? 'MEMBER'} data-sentry-element="PlanExpirationAlertContainer" data-sentry-source-file="SubscribeUserPlan.tsx" />
    </>;
};
export const SubscribeUserPlanContainer = () => {
  return <NoSSR data-sentry-element="NoSSR" data-sentry-component="SubscribeUserPlanContainer" data-sentry-source-file="SubscribeUserPlan.tsx">
      <SubscribeUserPlan data-sentry-element="SubscribeUserPlan" data-sentry-source-file="SubscribeUserPlan.tsx" />
    </NoSSR>;
};